export interface JsonInput {
  data: any;
  source: string;
  isValid: boolean;
  error?: string;
}

export interface TableData {
  columns: string[];
  rows: Record<string, any>[];
}

export interface SortConfig {
  key: string;
  direction: 'asc' | 'desc';
}

export class JsonParser {
  parse(jsonString: string): JsonInput {
    try {
      const data = JSON.parse(jsonString);
      return {
        data,
        source: jsonString,
        isValid: true
      };
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Invalid JSON');
    }
  }

  flatten(data: any): Record<string, any>[] {
    const seen = new WeakSet();

    const flattenObject = (obj: any, prefix = ''): Record<string, any> => {
      // Handle circular references
      if (obj && typeof obj === 'object') {
        if (seen.has(obj)) {
          throw new Error('Circular reference detected');
        }
        seen.add(obj);
      }

      const flattened: Record<string, any> = {};

      if (obj === null || obj === undefined) {
        return { [prefix || 'value']: obj };
      }

      if (Array.isArray(obj)) {
        // Handle arrays of objects - return individual flattened objects
        if (obj.every(item => typeof item === 'object' && item !== null)) {
          return obj.reduce((acc, item, _index) => {
            const itemFlattened = flattenObject(item);
            Object.entries(itemFlattened).forEach(([key, value]) => {
              acc[key] = value;
            });
            return acc;
          }, {} as Record<string, any>);
        }
        // Handle arrays of primitives
        return { [prefix || 'value']: obj.join(', ') };
      }

      if (typeof obj === 'object') {
        Object.entries(obj).forEach(([key, value]) => {
          const newKey = prefix ? `${prefix}.${key}` : key;

          if (value === null || value === undefined) {
            flattened[newKey] = value;
          } else if (Array.isArray(value)) {
            if (value.every(item => typeof item === 'object' && item !== null)) {
              // Array of objects - flatten each one without array indices
              const flattenedArray = value.map(item => flattenObject(item, newKey));
              // Return flattened array as separate objects
              return flattenedArray[0] || {};
            } else if (value.every(item => typeof item !== 'object')) {
              // Array of primitives
              flattened[newKey] = value.join(', ');
            } else {
              // Mixed array
              flattened[newKey] = JSON.stringify(value);
            }
          } else if (typeof value === 'object') {
            // Nested object
            const nestedFlattened = flattenObject(value, newKey);
            Object.entries(nestedFlattened).forEach(([nestedKey, nestedValue]) => {
              flattened[nestedKey] = nestedValue;
            });
          } else {
            // Primitive value
            flattened[newKey] = value;
          }
        });
        return flattened;
      }

      // Handle empty objects and arrays
      if (obj && typeof obj === 'object') {
        if (Array.isArray(obj) && obj.length === 0) {
          return { [prefix ? `${prefix}` : 'value']: '' };
        }
        if (!Array.isArray(obj) && Object.keys(obj).length === 0) {
          return { [prefix ? `${prefix}` : 'value']: '{}' };
        }
      }

      // Primitive value
      return { [prefix || 'value']: obj };
    };

    // Handle different input types
    if (Array.isArray(data)) {
      return data.map((item, _index) => {
        if (typeof item === 'object' && item !== null) {
          return flattenObject(item);
        } else {
          return { value: item };
        }
      });
    } else if (typeof data === 'object' && data !== null) {
      return [flattenObject(data)];
    } else {
      return [{ value: data }];
    }
  }

  prepareTableData(data: any): TableData {
    let rows: Record<string, any>[];

    if (Array.isArray(data)) {
      // If it's an array, flatten each item
      rows = data.flatMap(item => {
        if (typeof item === 'object' && item !== null) {
          return this.flatten(item);
        } else {
          return [{ value: item }];
        }
      });
    } else if (typeof data === 'object' && data !== null) {
      // If it's a single object, flatten it
      rows = this.flatten(data);
    } else {
      // Primitive value
      rows = [{ value: data }];
    }

    // Handle empty objects and arrays
    rows.forEach(row => {
      if (row.hasOwnProperty('emptyObject')) {
        row.emptyObject = '{}';
      }
      if (row.hasOwnProperty('emptyArray')) {
        row.emptyArray = '';
      }
    });

    // Get all unique columns across all rows
    const allKeys = new Set<string>();
    rows.forEach(row => {
      Object.keys(row).forEach(key => allKeys.add(key));
    });

    // Ensure all rows have the same structure
    const columns = Array.from(allKeys).sort();

    const normalizedRows = rows.map(row => {
      const normalizedRow: Record<string, any> = {};
      columns.forEach(col => {
        normalizedRow[col] = row[col] ?? '';
      });
      return normalizedRow;
    });

    return {
      columns,
      rows: normalizedRows
    };
  }

  sortTableData(tableData: TableData, sortConfig: SortConfig): TableData {
    const { key, direction } = sortConfig;
    const sortedRows = [...tableData.rows].sort((a, b) => {
      const aValue = a[key];
      const bValue = b[key];

      // Handle different data types
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      // Try numeric comparison first
      const aNum = Number(aValue);
      const bNum = Number(bValue);

      if (!isNaN(aNum) && !isNaN(bNum)) {
        return direction === 'asc' ? aNum - bNum : bNum - aNum;
      }

      // Fall back to string comparison
      const aStr = String(aValue);
      const bStr = String(bValue);

      return direction === 'asc'
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr);
    });

    return {
      ...tableData,
      rows: sortedRows
    };
  }

  validateJson(data: any): { isValid: boolean; error?: string } {
    if (data === null) {
      return { isValid: false, error: 'JSON cannot be null' };
    }

    if (Array.isArray(data) && data.length === 0) {
      return { isValid: false, error: 'JSON array cannot be empty' };
    }

    if (typeof data === 'object' && Object.keys(data).length === 0) {
      return { isValid: false, error: 'JSON object cannot be empty' };
    }

    return { isValid: true };
  }

  exportToCSV(tableData: TableData): string {
    if (tableData.rows.length === 0) return '';

    const headers = tableData.columns.join(',');
    const rows = tableData.rows.map(row =>
      tableData.columns.map(col => {
        const value = row[col];
        if (value === null || value === undefined) return '';
        const stringValue = String(value);
        // Escape CSV values
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      }).join(',')
    );

    return [headers, ...rows].join('\n');
  }

  exportToMarkdown(tableData: TableData): string {
    if (tableData.rows.length === 0) return '';

    const headers = tableData.columns.map(col => `| ${col} `).join('') + '|';
    const separator = tableData.columns.map(() => '| --- ').join('') + '|';
    const rows = tableData.rows.map(row =>
      tableData.columns.map(col => `| ${row[col] ?? ''} `).join('') + '|'
    );

    return [headers, separator, ...rows].join('\n');
  }

  exportToJSON(tableData: TableData): string {
    return JSON.stringify(tableData.rows, null, 2);
  }
}